import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { CompanyProductPhaseStage } from 'src/app/models/company_product_phase_stage.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UtilsService } from 'src/app/services/utils.service';
import { UserService } from 'src/app/services/user.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { NgStyle } from '@angular/common';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { CompanyProductSettingsDialogComponent } from './company-product-settings-dialog/company-product-settings-dialog.component';
import { CompanyProductRoleMembersLabelPipe } from 'src/app/pipes/company-product-role-members-label.pipe';

@Component({
  selector: 'app-company-products',
  templateUrl: './company-products.component.html',
  styleUrls: ['./company-products.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButton,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    NgStyle,
    RouterLink,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    CompanyProductRoleMembersLabelPipe,
  ],
})
export class CompanyProductsComponent extends IndexTemplateComponent {
  private api = inject(ApiService);
  private userService = inject(UserService);
  private employee = inject(EmployeeService);
  permissions = inject(PermissionsService);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly dialog = inject(MatDialog);

  displayedColumns: string[];
  dataSource: MatTableDataSource<CompanyProduct> = new MatTableDataSource<CompanyProduct>();

  filterForm: UntypedFormGroup;
  stages: CompanyProductPhaseStage[] = [];

  override filterFormExtraParams: any = {
    ro_is_active: [
      this.activatedRoute.snapshot.queryParamMap.get('ro_is_active') != null
        ? +(this.activatedRoute.snapshot.queryParamMap.get('ro_is_active') as any)
        : 'true',
    ],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    this.fetchStages();
    super.ngOnInit();

    this.filterColumnsForRol();
    this.listenShareScreenMode();
  }

  getCompanyProductDetailLink(company_product_id: number): (string | number)[] {
    if (this.permissions.isClient()) {
      return ['/clients', 'company-products', company_product_id];
    }

    if (this.permissions.isPartner()) {
      return ['/partners', 'company-products', company_product_id];
    }

    return ['/employees', 'development', 'company-products', company_product_id];
  }

  shareScreen(company_product_id: number) {
    this.employee.startMeetingMode(company_product_id);
  }

  openCompanyProductSettings(company_product: CompanyProduct) {
    this.dialog.open(CompanyProductSettingsDialogComponent, {
      width: '600px',
      data: { company_product_id: company_product.id },
    });
  }

  override fetchData() {
    const query: any = this.filterForm.value;
    this.api
      .getCompanyProductsPaged({
        ...query,
        with: 'company_product_phases,company.active_account_manager_members.user,active_company_product_members.user,active_company_product_members.company_product_user_member_role,active_contracts..current_contract_variable_condition',
        append: 'ro_is_active',
      })
      .subscribe(data => {
        this.dataSource.data = data.data.map(company_product => ({
          ...company_product,
          account_manager_label: this.buildAccountManagerLabel(company_product),
        }));
        this.setMetadata(+data.meta.total, data.meta.current_page - 1, data.meta.per_page);
      });
  }

  private filterColumnsForRol() {
    if (this.permissions.isWorker()) {
      this.displayedColumns = ['title', 'company', 'product_manager', 'product_designer', 'tech_lead', 'account_manager', 'actions'];
    } else if (this.permissions.isClient()) {
      this.displayedColumns = ['title', 'product_manager', 'product_designer', 'tech_lead', 'account_manager'];
    } else if (this.permissions.isPartner()) {
      this.displayedColumns = ['title', 'product_manager', 'product_designer', 'tech_lead', 'account_manager'];
    }
  }

  private fetchStages() {
    this.api.getCompanyProductPhaseStages().subscribe(data => {
      this.stages = data;
    });
  }

  private listenShareScreenMode() {
    if (this.permissions.isWorker()) {
      this.employee.meetingModeCompanyProduct.subscribe(data => {
        if (data != null) {
          this.router.navigate(['/employees', 'development', 'company-products', data]);
        }
      });
    }
  }

  private buildAccountManagerLabel(company_product: CompanyProduct): string {
    const account_managers = company_product.company?.active_account_manager_members ?? [];

    if (account_managers.length === 0) {
      return '-';
    }

    return account_managers.map(member => `${member.user.name} ${member.user.surnames}`.trim()).join(', ');
  }
}
